import { useEffect, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { timeAgo } from '../lib/agentCommands';
import { formatDateTime } from '../lib/format';
import { useUrlState } from '../lib/useUrlState';
import {
  ATLAS_CAP,
  ATLAS_WARN,
  ATLAS_UNMAPPED,
  PROJECTS_OVERVIEW,
  capacity,
  levelColor,
  type AtlasProjectOverview,
  type UnmappedCluster,
} from '../lib/atlas';

/**
 * `/atlas-whitelist` — the index. Capacity first, one row per project.
 *
 * Every MongoDB Atlas project caps its IP access list at 200 entries. Past that,
 * `POST …/accessList` is REJECTED, so the next node to come up never reaches the
 * database. The old page could not answer "is any project near the cap" at all: it
 * showed one project at a time behind a `<select>`, and the only number on screen
 * was a pager total under a 25-row table.
 *
 * Nothing here calls Atlas. The counts are the snapshot written whenever the list
 * was last legitimately read, and every row states how old that is — see
 * `AtlasWhitelistService.listProjectOverview` for the trade.
 */

const CREATE_PROJECT = gql`
  mutation CreateAtlasProject($input: CreateAtlasProjectInput!) {
    createAtlasProject(input: $input) {
      projectKey
      hasCredentials
    }
  }
`;

export function AtlasWhitelistPage() {
  const navigate = useNavigate();
  // Old links were `/atlas-whitelist?project=prod-atlas`. Those are in Slack threads
  // and runbooks, so they keep working — as a redirect to the route that now owns
  // that view, rather than as a second way to render it.
  const [legacyProject, setLegacyProject] = useUrlState('project');
  useEffect(() => {
    if (!legacyProject) return;
    setLegacyProject('');
    navigate(`/atlas-whitelist/${encodeURIComponent(legacyProject)}`, { replace: true });
  }, [legacyProject, setLegacyProject, navigate]);

  const { data, loading, error, refetch } = useQuery<{
    atlasProjectsOverview: AtlasProjectOverview[];
  }>(PROJECTS_OVERVIEW, { fetchPolicy: 'cache-and-network' });
  const unmapped = useQuery<{ atlasUnmappedClusters: UnmappedCluster[] }>(ATLAS_UNMAPPED, {
    fetchPolicy: 'cache-and-network',
  });
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useUrlState('q');

  const projects = data?.atlasProjectsOverview ?? [];
  const matching = projects.filter((p) => `${p.displayName ?? ''} ${p.projectKey}`.toLowerCase().includes(query.trim().toLowerCase()));
  // Ignored clusters are excluded from the count the footer advertises — that is
  // the entire point of being able to ignore one.
  const unmappedActive = (unmapped.data?.atlasUnmappedClusters ?? []).filter((c) => !c.ignored);

  // The worst project decides whether the banner appears at all. Sorting by live
  // count rather than by percentage-of-anything keeps it comparable to the cap.
  const warned = projects
    .filter((p) => p.liveCount != null && p.liveCount >= ATLAS_WARN)
    .sort((a, b) => (b.liveCount ?? 0) - (a.liveCount ?? 0));
  const worst = warned[0];

  return (
    <div className="page page--wide operations-page atlas-overview-page">
      <div className="detail-head">
        <h1>Atlas IP whitelist</h1>
        <span className="badge quiet">
          {projects.length} project{projects.length === 1 ? '' : 's'}
        </span>
        <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void refetch().catch(() => undefined)}>
          Refresh
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Close' : 'Add project'}
        </button>
      </div>

      <p className="detail-desc">Monitor access-list capacity, pending IPs, and agent health across Atlas projects.</p>
      <p className="operations-note">{ATLAS_CAP} entries per project · Counts reflect the last recorded access-list read.</p>

      {showAdd && (
        <AddProjectForm
          onDone={(key) => {
            setShowAdd(false);
            void refetch();
            navigate(`/atlas-whitelist/${encodeURIComponent(key)}`);
          }}
        />
      )}

      {error && (
        <>
          <p className="error">Couldn&apos;t load the Atlas projects — the API didn&apos;t respond.</p>
          <p className="muted">
            Nothing was changed in Atlas; this page only reads. If it persists, ask in
            #devops-infra.
          </p>
        </>
      )}

      {worst && <CapAlert project={worst} />}

      {loading && !projects.length ? (
        <div className="skeleton-row-group">
          {[0, 1, 2].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      ) : !projects.length && !error ? (
        <EmptyState
          icon="shield"
          title="No Atlas projects yet"
          body="Add one with Add project above — its Atlas project id and an OAuth client id + secret. The secret is encrypted before it is stored, never kept in plaintext."
        />
      ) : (
        <section className="atlas-card operations-panel" aria-label="Atlas projects">
          <div className="filter-row"><input type="search" aria-label="Search Atlas projects" placeholder="Search project name or key" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && <button className="mini-btn" onClick={() => setQuery('')}>Clear search</button>}
          <span className="filter-count">{matching.length} of {projects.length} projects</span></div>
          <div className="operations-table-scroll" role="region" aria-label="Atlas project table" tabIndex={0}>
          <table className="atlas-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Access list usage</th>
                <th>Waiting to push</th>
                <th>Clusters</th>
                <th>Agents</th>
                <th>Last sync</th>
              </tr>
            </thead>
            <tbody>
              {!matching.length && <tr><td colSpan={6}>No projects match your search.</td></tr>}
              {matching.map((p) => (
                <ProjectRow key={p.projectKey} p={p} />
              ))}
            </tbody>
          </table>
          </div>
        </section>
      )}

      <div className="atlas-index-foot">
        <Link to="/atlas-whitelist/mappings">Cluster mappings</Link>
        {unmappedActive.length > 0 && (
          <span className="atlas-foot-warn">
            <span className="atlas-dot" style={{ background: 'var(--amber)' }} />
            {unmappedActive.length} cluster{unmappedActive.length === 1 ? '' : 's'} with no Atlas
            project
          </span>
        )}
      </div>
    </div>
  );
}

/** Shown only at or past the warn line. Red past crit, because the difference
 *  between "clean up soon" and "the next push fails" is the whole message. */
function CapAlert({ project }: { project: AtlasProjectOverview }) {
  const cap = capacity(project.liveCount, project.pendingCount);
  const crit = cap.rejected || cap.level === 'crit';
  return (
    <div className={`atlas-alert${crit ? ' atlas-alert--crit' : ''}`}>
      <Icon name="alert" size={17} />
      <div className="atlas-alert__body">
        <div className="atlas-alert__title">
          {project.displayName || project.projectKey} is at {cap.live} of {ATLAS_CAP} entries —{' '}
          {cap.slotsLeft} slot{cap.slotsLeft === 1 ? '' : 's'} left
        </div>
        <div className="atlas-alert__text">
          {cap.pending > 0 ? (
            <>
              {cap.pending} reported IP{cap.pending === 1 ? ' is' : 's are'} still waiting to be
              pushed, which would take it to {cap.projected}.{' '}
              {cap.rejected
                ? `That is past the ${ATLAS_CAP} limit, so the next reconcile will be rejected outright — new nodes will not reach the database.`
                : `Alerts start at ${ATLAS_WARN} so there is room to clean up before Atlas starts rejecting adds.`}
            </>
          ) : (
            <>
              Nothing is waiting to be pushed right now, but only {cap.slotsLeft} slot
              {cap.slotsLeft === 1 ? '' : 's'} remain for the next nodes to come up.
            </>
          )}
        </div>
      </div>
      <Link className="btn" to={`/atlas-whitelist/${encodeURIComponent(project.projectKey)}`}>
        Review project
      </Link>
    </div>
  );
}

function ProjectRow({ p }: { p: AtlasProjectOverview }) {
  const cap = capacity(p.liveCount, p.pendingCount);
  const color = levelColor(cap.level);
  const stale = p.agentsStale > 0;

  return (
    <tr>
      <td>
        <Link className="atlas-proj" to={`/atlas-whitelist/${encodeURIComponent(p.projectKey)}`}>
          {p.displayName || p.projectKey}
          {!p.isActive && <span className="muted"> (inactive)</span>}
        </Link>
        <div className="atlas-sub">{p.projectKey}</div>
      </td>

      <td>
        <div className="atlas-usage">
          {/* A project with no credentials has UNKNOWN usage. A zero-width bar over a
              dim track says that; an empty bar over a normal track would claim it is
              using none of its 200 slots. */}
          <span className="atlas-bar">
            <span
              className="atlas-bar__live"
              style={{ width: `${cap.livePct}%`, background: cap.known ? color : 'transparent' }}
            />
            <span className="atlas-bar__pending" style={{ width: `${cap.pendingPct}%` }} />
          </span>
          <span className="atlas-usage__n" style={{ color: cap.level === 'ok' ? undefined : color }}>
            {cap.known ? `${cap.live} / ${ATLAS_CAP}` : '—'}
          </span>
        </div>
        <div className="atlas-sub">
          {!cap.known
            ? 'Usage unavailable — list not read'
            : cap.level !== 'ok'
              ? `${cap.slotsLeft} slot${cap.slotsLeft === 1 ? '' : 's'} left`
              : p.liveCountAt
                ? `Read ${timeAgo(p.liveCountAt)}`
                : 'healthy'}
        </div>
      </td>

      <td style={{ color: cap.pending && cap.level !== 'ok' ? 'var(--amber)' : undefined }}>
        {cap.pending ? `+${cap.pending}` : '—'}
      </td>

      <td>{p.clusterCount}</td>

      <td>
        <span className="atlas-dot" style={{ background: stale ? 'var(--bad)' : 'var(--green)' }} />
        <span style={{ color: stale ? 'var(--bad)' : undefined }}>
          {p.agentsTotal === 0
            ? '—'
            : stale
              ? `${p.agentsStale} silent · ${p.agentsTotal - p.agentsStale} live`
              : `${p.agentsTotal} live`}
        </span>
      </td>

      <td className="muted" title={p.lastSyncAt ? formatDateTime(p.lastSyncAt) : undefined}>
        <span
          className="atlas-dot"
          style={{
            background: p.lastSyncStatus === 'ok' ? 'var(--green)' : p.lastSyncAt ? 'var(--amber)' : 'var(--faint-deep)',
          }}
        />
        {p.lastSyncAt ? timeAgo(p.lastSyncAt) : 'Never'}
      </td>
    </tr>
  );
}

function AddProjectForm({ onDone }: { onDone: (projectKey: string) => void }) {
  const [form, setForm] = useState({
    projectKey: '',
    projectId: '',
    clientId: '',
    clientSecret: '',
    displayName: '',
    description: '',
  });
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const [create, createState] = useMutation<{ createAtlasProject: { projectKey: string } }>(
    CREATE_PROJECT,
    { onCompleted: (d) => onDone(d.createAtlasProject.projectKey) },
  );

  const ready =
    form.projectKey.trim() && form.projectId.trim() && form.clientId.trim() && form.clientSecret.trim();

  const submit = () =>
    create({
      variables: {
        input: {
          projectKey: form.projectKey.trim(),
          projectId: form.projectId.trim(),
          clientId: form.clientId.trim(),
          clientSecret: form.clientSecret,
          displayName: form.displayName.trim() || null,
          description: form.description.trim() || null,
        },
      },
    });

  const field = (
    label: string,
    key: keyof typeof form,
    placeholder: string,
    hint?: string,
    secret?: boolean,
  ) => (
    <label style={{ display: 'block', marginBottom: '0.6rem' }}>
      <div className="kv-k" style={{ marginBottom: 2 }}>
        {label}
      </div>
      <input
        className="class-select"
        type={secret ? 'password' : 'text'}
        placeholder={placeholder}
        value={form[key]}
        onChange={set(key)}
        style={{ width: '100%', maxWidth: 460 }}
      />
      {hint && (
        <div className="muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
          {hint}
        </div>
      )}
    </label>
  );

  return (
    <section className="detail-desc" style={{ padding: '1rem', marginBottom: '1rem', maxWidth: 520 }}>
      <h3 style={{ marginTop: 0 }}>Add Atlas project</h3>
      {field('Project key *', 'projectKey', 'prod-atlas', 'Unique identifier used by agents')}
      {field('Atlas project id *', 'projectId', '64a1b2c3d4e5f6789012345', 'Atlas → Project Settings → General')}
      {field('OAuth client id *', 'clientId', 'mdb_sa_id_…', 'Atlas → Access Manager → Applications')}
      {field('OAuth client secret *', 'clientSecret', 'Enter client secret', 'Stored encrypted (never plaintext)', true)}
      {field('Display name', 'displayName', 'Production Atlas')}
      {field('Description', 'description', 'Production MongoDB Atlas project')}
      {createState.error && <p className="error">{createState.error.message}</p>}
      <div className="btn-row" style={{ marginTop: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!ready || createState.loading}
          onClick={submit}
        >
          {createState.loading ? 'Creating…' : 'Create'}
        </button>
      </div>
    </section>
  );
}
